import React, { useState, useRef, useEffect } from "react";
import { FileIcon, defaultStyles } from "react-file-icon";
import { useCampaign } from "../../contexts/CampaignContext";

const SESSION_KEY = "campaign_info_form";

const NewCampaignInfo = ({ onNext }) => {
  // 使用Context
  const { state, actions, utils } = useCampaign();
  
  // 控制可折叠section的展开状态
  const [openLanding, setOpenLanding] = useState(true);
  const [openMedia, setOpenMedia] = useState(true);
  // 文件上传
  const fileInputRef = useRef(null);

  // 受控表单项 - 从Context获取
  const campaignName = state.campaignInfo.campaignName;
  const productDescription = state.campaignInfo.productDescription;
  const landingPage = state.campaignInfo.landingPage;
  const files = state.campaignInfo.files;

  // 初始化时从 sessionStorage 恢复
  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        
        // 更新Context中的基本信息
        actions.updateCampaignInfo({
          campaignName: data.campaignName || '',
          productDescription: data.productDescription || '',
          landingPage: data.landingPage || ''
        });
        
        // 检查是否有文件信息但没有实际文件对象
        if (Array.isArray(data.filesInfo) && data.filesInfo.length > 0 && files.length === 0) {
          console.log('⚠️ File information found in session storage but actual file objects are lost.');
          console.log('📋 Lost files:', data.filesInfo.map(f => f.name));
          console.log('💡 Users need to re-select files to upload them.');
          // 不再创建占位符，避免困惑
        }
      } catch {}
    }
  }, []);

  // 表单内容变化时写入 sessionStorage（只存储文件信息，不存储文件本身）
  useEffect(() => {
    const realFiles = files.filter(f => f instanceof File);
    
    console.log('=== NewCampaignInfo 文件状态更新 ===');
    console.log('当前 files 状态:', files.map(f => ({
      name: f.name,
      type: f.type,
      isFile: f instanceof File,
      isPlaceholder: f.isPlaceholder || false
    })));
    console.log('过滤后的真实文件数量:', realFiles.length);
    
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        campaignName,
        productDescription,
        landingPage,
        filesInfo: files.map(f => ({ name: f.name, type: f.type, size: f.size })), // 只用于显示
      })
    );
  }, [campaignName, productDescription, landingPage, files]);

  const handleSelectFiles = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };
  const handleFilesChange = (e) => {
    const newFiles = Array.from(e.target.files);
    console.log('✅ New files selected:', newFiles.map(f => f.name));
    
    // 只保留真正的File对象，然后添加新文件
    const realFiles = files.filter(f => f instanceof File);
    const updatedFiles = [...realFiles, ...newFiles];
    
    console.log('📁 Files updated:', updatedFiles.map(f => ({
      name: f.name,
      isFile: f instanceof File
    })));
    
    actions.setFiles(updatedFiles);
  };
  
  const handleRemoveFile = (idx) => {
    const updatedFiles = files.filter((_, i) => i !== idx);
    console.log('🗑️ File removed at index:', idx);
    console.log('📁 Remaining files:', updatedFiles.map(f => f.name));
    actions.setFiles(updatedFiles);
  };

  // 获取文件扩展名
  const getExt = (name) => {
    const parts = name.split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
  };

  // 验证必填字段
  const isFormValid = () => {
    return (
      campaignName.trim() !== "" &&
      productDescription.trim() !== "" &&
      landingPage.trim() !== ""
    );
  };

  return (
    <form className="campaign-form">
      <header className="form-header">
        <div>
          <h2 className="form-title">Campaign Info</h2>
          <p className="form-subtitle">
            Complete the form to set up your campaign
          </p>
        </div>
        <span className="step-indicator">Step 1 of 3</span>
      </header>

      {/* Campaign Name Section */}
      <section className="form-section">
        <div className="section-header">
          <img
            src="https://cdn.builder.io/api/v1/image/assets/TEMP/c7ed12877f10d825d905257fc33668dc8a4fde4e?placeholderIfAbsent=true&apiKey=dc2f6a1e66164bfb9d194f96d43a57dd"
            alt=""
            className="section-icon"
          />
          <div className="section-title-group">
            <h3 className="section-title">Campaign Name</h3>
            <p className="section-subtitle">
              Complete the form to set up your campaign
            </p>
          </div>
        </div>
        <input
          type="text"
          placeholder="Enter Input"
          className="text-input"
          aria-label="Campaign name input"
          value={campaignName}
          onChange={e => actions.updateCampaignInfo({ campaignName: e.target.value })}
          required
        />
        <p className="helper-text">
          Choose a campaign name that includes your brand, product, or service
          and clearly describes the campaign for easy reference later
        </p>
      </section>

      {/* Product Description Section */}
      <section className="form-section">
        <div className="section-header">
          <img
            src="https://cdn.builder.io/api/v1/image/assets/TEMP/16c7ab0b2d0b3fff54f6810bda5008f6136b9233?placeholderIfAbsent=true&apiKey=dc2f6a1e66164bfb9d194f96d43a57dd"
            alt=""
            className="section-icon rounded"
          />
          <div className="section-title-group">
            <h3 className="section-title">Product/ Service Description</h3>
            {/* <p className="section-subtitle">
              Tell us about your product/service and your ideal customers
            </p> */}
          </div>
        </div>
        <textarea
          placeholder="Tell us about your product/service and your ideal customers"
          className="textarea-input"
          aria-label="Product description input"
          value={productDescription}
          onChange={e => actions.updateCampaignInfo({ productDescription: e.target.value })}
          required
          style={{
            resize: 'vertical',
            minHeight: '120px',
            height: '120px'
          }}
        />
      </section>

      {/* Landing Page Section - 可折叠 */}
      <section className="form-section">
        <div
          className="section-header collapsible-header"
          onClick={() => setOpenLanding((v) => !v)}
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <img
            src="https://cdn.builder.io/api/v1/image/assets/TEMP/a4d90588b727d7199d73c1c1717a55809f7e891c?placeholderIfAbsent=true&apiKey=dc2f6a1e66164bfb9d194f96d43a57dd"
            alt=""
            className="section-icon rounded"
            style={{ marginRight: 12 }}
          />
          <div className="collapsible-title-group">
            <div className="title-with-icon">
              <h3 className="section-title">Your Landing Page</h3>
              <img
                src="https://cdn.builder.io/api/v1/image/assets/TEMP/0d3615d553b9524ac6965a48ff8d5fc5e0a71904?placeholderIfAbsent=true&apiKey=dc2f6a1e66164bfb9d194f96d43a57dd"
                alt=""
                className="info-icon"
                style={{ transform: "rotate(180deg)" }}
              />
            </div>
            <p className="section-subtitle">
              Where would you like our creators to send their audience
            </p>
          </div>
        </div>
        {openLanding && (
          <>
            <input
              type="url"
              placeholder="Enter URL"
              className="text-input"
              aria-label="Landing page URL input"
              value={landingPage}
              onChange={e => actions.updateCampaignInfo({ landingPage: e.target.value })}
              required
            />
            <p className="helper-text">
              Choose a campaign name that includes your brand, product, or
              service and clearly describes the campaign for easy reference
              later
            </p>
          </>
        )}
      </section>

      {/* Media Assets Section - 可折叠 */}
      <section className="form-section">
        <div
          className="section-header collapsible-header"
          onClick={() => setOpenMedia((v) => !v)}
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <img
            src="https://cdn.builder.io/api/v1/image/assets/TEMP/15945d9909d636665931c9d92895d03fbd264d54?placeholderIfAbsent=true&apiKey=dc2f6a1e66164bfb9d194f96d43a57dd"
            alt=""
            className="section-icon rounded"
            style={{ marginRight: 12 }}
          />
          <div className="collapsible-title-group">
            <div className="title-with-icon">
              <h3 className="section-title">Campaign Media Assets</h3>
              <img
                src="https://cdn.builder.io/api/v1/image/assets/TEMP/0d3615d553b9524ac6965a48ff8d5fc5e0a71904?placeholderIfAbsent=true&apiKey=dc2f6a1e66164bfb9d194f96d43a57dd"
                alt=""
                className="info-icon"
                style={{ transform: "rotate(180deg)" }}
              />
            </div>
            <p className="section-subtitle">
              Upload images and videos that showcase your product or brand
              guidelines
            </p>
          </div>
        </div>
        {openMedia && (
          <>
            <div className="file-upload-container">
              {/* 文件预览区始终显示 */}
              <div className="file-preview-area">
                {files.length === 0 && (
                  <span style={{ color: "#bbb", fontSize: 14 }}>
                    &nbsp;No files selected
                  </span>
                )}
                {files.length > 0 &&
                  files.map((file, idx) => {
                    const ext = getExt(file.name);
                    return (
                      <div key={idx} className="file-preview-item">
                        <FileIcon
                          extension={ext}
                          {...(defaultStyles[ext] || defaultStyles.default)}
                          style={{ marginRight: 8, width: 24, height: 24 }}
                        />
                        <span className="file-preview-name">{file.name}</span>
                        <button
                          type="button"
                          style={{
                            marginLeft: 4,
                            color: "#e53935",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            fontSize: 16,
                          }}
                          title="Remove"
                          onClick={() => handleRemoveFile(idx)}
                        >
                          &times;
                        </button>
                      </div>
                    );
                  })}
              </div>
              <button
                type="button"
                className="select-files-btn"
                onClick={handleSelectFiles}
              >
                Select Files
              </button>
              <input
                type="file"
                multiple
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFilesChange}
              />
            </div>
            <p className="helper-text">
              Choose a campaign name that includes your brand, product, or
              service and clearly describes the campaign for easy reference
              later
            </p>
          </>
        )}
      </section>

      <div className="form-actions">
        <button 
          type="button" 
          className="next-button" 
          onClick={() => {
            if (isFormValid() && onNext) {
              onNext();
            }
          }}
          disabled={!isFormValid()}
          style={{ 
            opacity: isFormValid() ? 1 : 0.5,
            cursor: isFormValid() ? 'pointer' : 'not-allowed'
          }}
        >
          Next &gt;
        </button>
      </div>
    </form>
  );
};

export default NewCampaignInfo;
